import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, todayStr } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Wallet, Landmark } from "lucide-react";

export default function CashConfirmation() {
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [bank, setBank] = useState("");
  const [bankBusy, setBankBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/dashboard?date=${date}`).then((r) => {
      setRows(r.data.drivers);
      const m = {};
      r.data.drivers.forEach((d) => { m[d.driver_id] = { cash: d.eod ? d.eod.cash_confirmed : "", reason: d.eod ? d.eod.reason : "" }; });
      setInputs(m);
    }).finally(() => setLoading(false));
    api.get(`/bank-deposit?date=${date}`).then((r) => setBank(r.data?.amount ? String(r.data.amount) : ""));
  };
  useEffect(load, [date]);

  const saveBank = async () => {
    if (bank === "" || bank === undefined) { toast.error("Enter the amount deposited in bank"); return; }
    setBankBusy(true);
    try {
      await api.post("/bank-deposit", { date, amount: parseFloat(bank) });
      toast.success("Bank deposit saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBankBusy(false); }
  };

  const confirm = async (d) => {
    const inp = inputs[d.driver_id] || {};
    if (inp.cash === "" || inp.cash === undefined) { toast.error("Enter confirmed cash"); return; }
    try {
      await api.post("/eod/confirm", { driver_id: d.driver_id, date, cash_confirmed: parseFloat(inp.cash), reason: inp.reason || "" });
      toast.success(`Confirmed for ${d.driver_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold flex items-center gap-2"><Wallet className="h-6 w-6 text-primary" /> End-of-day cash confirmation</h1>
          <p className="text-sm text-muted-foreground">Enter the cash each driver physically handed over. Shortages are flagged.</p>
        </div>
        <DateField value={date} onChange={setDate} className="w-auto" testid="cash-date-input" />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="cash-confirmation-table">
            <TableHeader>
              <TableRow><TableHead>Driver</TableHead><TableHead className="text-right">Cash expected</TableHead><TableHead>Cash handed over</TableHead><TableHead className="text-right">Difference</TableHead><TableHead>Reason (if short)</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && rows.map((d) => {
                const inp = inputs[d.driver_id] || {};
                const delta = inp.cash !== "" && inp.cash !== undefined ? parseFloat(inp.cash) - d.cash_total : null;
                return (
                  <TableRow key={d.driver_id} className={delta !== null && Math.abs(delta) > 0.5 ? "bg-rose-50/70" : ""}>
                    <TableCell className="font-medium">{d.driver_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(d.cash_total)}</TableCell>
                    <TableCell><Input type="number" value={inp.cash} onChange={(e) => setInputs({ ...inputs, [d.driver_id]: { ...inp, cash: e.target.value } })} className="w-32" data-testid={`cash-input-${d.driver_id}`} /></TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${delta !== null && Math.abs(delta) > 0.5 ? "text-rose-600" : "text-emerald-600"}`}>{delta !== null ? inr(delta) : "—"}</TableCell>
                    <TableCell><Input value={inp.reason} onChange={(e) => setInputs({ ...inputs, [d.driver_id]: { ...inp, reason: e.target.value } })} placeholder="Reason" className="w-40" data-testid={`cash-reason-${d.driver_id}`} /></TableCell>
                    <TableCell className="text-right"><Button size="sm" onClick={() => confirm(d)} data-testid={`cash-confirm-${d.driver_id}`}>{d.eod ? "Update" : "Confirm"}</Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="p-5" data-testid="bank-deposit-card">
        <div className="flex items-center gap-2">
          <Landmark className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Cash deposited in bank today</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Enter the total cash deposited into the bank for this day. This shows on the owner's dashboard.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
            <Input
              type="number"
              inputMode="numeric"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              placeholder="0"
              className="w-48 pl-7 h-11 text-base"
              data-testid="bank-deposit-input"
            />
          </div>
          <Button onClick={saveBank} disabled={bankBusy} className="h-11" data-testid="bank-deposit-save-button">
            {bankBusy ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
